import { useTheme } from "@mui/material";
import { LexicalEditor } from "lexical";
import React, { CSSProperties, JSX } from "react";
import { ResizeDirection, useImageResize } from "../hooks/useImageResize";

interface ResizeHandle extends ResizeDirection {
  id: string;
  position: CSSProperties;
}

const HANDLES: ResizeHandle[] = [
  { cursor: "ns-resize", id: "n", position: { left: "50%", marginLeft: -5, top: -5 }, y: "top" },
  { cursor: "nesw-resize", id: "ne", position: { right: -5, top: -5 }, x: "right", y: "top" },
  { cursor: "ew-resize", id: "e", position: { marginTop: -5, right: -5, top: "50%" }, x: "right" },
  { cursor: "nwse-resize", id: "se", position: { bottom: -5, right: -5 }, x: "right", y: "bottom" },
  { cursor: "ns-resize", id: "s", position: { bottom: -5, left: "50%", marginLeft: -5 }, y: "bottom" },
  { cursor: "nesw-resize", id: "sw", position: { bottom: -5, left: -5 }, x: "left", y: "bottom" },
  { cursor: "ew-resize", id: "w", position: { left: -5, marginTop: -5, top: "50%" }, x: "left" },
  { cursor: "nwse-resize", id: "nw", position: { left: -5, top: -5 }, x: "left", y: "top" },
];

interface ImageResizerProps {
  editor: LexicalEditor;
  imageRef: { current: HTMLElement | null };
  onResizeStart: () => void;
  onResizeEnd: (width: number, height: number) => void;
}

/**
 * Renders the eight drag handles overlaid on a selected image. All resize
 * mechanics live in `useImageResize`; this component is purely presentational.
 */
export function ImageResizer({ editor, imageRef, onResizeStart, onResizeEnd }: ImageResizerProps): JSX.Element {
  const theme = useTheme();
  const { startResize } = useImageResize({ editor, imageRef, onResizeEnd, onResizeStart });

  const handleStyle: CSSProperties = {
    backgroundColor: theme.palette.info.light,
    border: "1px solid #fff",
    borderRadius: "50%",
    boxShadow: "0 0 0 1px rgba(0,0,0,0.2)",
    height: 10,
    position: "absolute",
    width: 10,
    zIndex: 10,
  };

  return (
    <div>
      {HANDLES.map((handle) => (
        <div
          key={handle.id}
          onPointerDown={(event) => startResize(event, handle)}
          style={{ ...handleStyle, ...handle.position, cursor: handle.cursor }}
        />
      ))}
    </div>
  );
}
